import { BCMSRecord, BCMSRecordProperty } from "bcms-react";
import { CheckingAccount, SavingsAccount, CODAccount, LoanAccount, LinkedAccount } from "../"
/**
* @class Account
* @classdesc Class definition for record type from model
* @param {OBJECT} record - record from model to init class
* @extends BCMSRecord
* @example const record = model.accounts[0];
* const account = new Account(record);
* @property {NUMBER} _id - The id of the Account
* @property {STRING} _name - The name of the account
* @property {STRING} _nickname - The custom name for the account
* @property {STRING} _number - the last four of account
* @property {STRING} _type - represents the account type
* @property {NUMBER} _typeID - ID of sub type for additional information
* @property {BOOLEAN} _canTransferFrom - can the account be transferred from
* @property {bcmsDate} _dateOpened - days in past account was opened
* @property {BOOLEAN} _isMobilePrimary - is this the primary mobile account
* @property {bcmsDate} _lastDownloadDate - number of days in the past for last download
* @property {STRING} _balanceLabel - The label for the balance
* @property {STRING} _secondaryBalanceLabel - The label for the secondary balance
* @property {STRING} _statementPreference - The statement preference of the account
*
* @description When a new "Account" is created in the model xml, 
* create a "mobileNickname" entry in the model xml as well.
* This is probably how the actual product works since the user 
* can't create a "Mobile Nickname" in the product,
* but they have access to mobile nickname entries they can edit.
* 
*/

export default class Account extends BCMSRecord {
  _id
  _name
  _nickname
  _number
  _type
  _canTransferFrom
  _dateOpened
  _isMobilePrimary
  _lastDownloadDate
  _balanceLabel
  _secondaryBalanceLabel
  _statementPreference
  _isExternal
  _institutionName
  _routingNumber
  _externalStatus
  _isBusiness

  constructor(record) {
    super(record)
  }
  /**    
  * @property id {STRING} - Returns the account for the appropriate type
  * @example const account = Account.initByType("1");
  * @memberof Account 
  */
  static initByType(id) {
    let account = Account.initByID(id);
    switch (account.type) {
      case "checking":
        return CheckingAccount.initByID(id);
      case "savings":
        return SavingsAccount.initByID(id);
      case "cod":
        return CODAccount.initByID(id);
      case "loan":
        return LoanAccount.initByID(id);
    }
  }

  /** 
  * @function initAll   
  * @static
  * @property {VOID} - No parameters
  * @description Returns all accounts with branding option "Show Business Accounts" 
  * (action script name: show_business_accounts_abi22) applied.
  * @example const accounts = Account.initAll();
  * @memberof Account 
  */
  static initAll() {
    const { options } = this._GlobalController.state.branding;
    const showBusinessAccounts = options.show_business_accounts_abi22 === "true";

    const filterByBusinessValue = item => {
      if(showBusinessAccounts) {
        return true;
      } else {
        if(item.isBusiness === "true") {
          return false;
        }
      }
      return true;
    }

    const accounts = this._GlobalController.state[this._RecordType].filter(filterByBusinessValue);

    return accounts.map((record) => {
        return this.initByType(record.id);
    });
  }

  /** 
  * @function initAllBusiness   
  * @static
  * @property {VOID} - No parameters
  * @description Returns all business accounts.
  * @example const accounts = Account.initAllBusiness();
  * @memberof Account 
  */
  static initAllBusiness() {
    const accounts = this._GlobalController.state[this._RecordType];

    return accounts.filter(item => item.isBusiness === "true").map((record) => {
        return this.initByType(record.id);
    });
  }

  /** 
  * @function initAllPersonal   
  * @static
  * @property {VOID} - No parameters
  * @description Returns all personal accounts.
  * @example const accounts = Account.initAllPersonal();
  * @memberof Account 
  */
  static initAllPersonal() {
    const accounts = this._GlobalController.state[this._RecordType];
    
    return accounts.filter(item => item.isBusiness !== "true").map((record) => {
        return this.initByType(record.id);
    });
  }

  /** 
  * @function initAllAsset   
  * @static
  * @property {VOID} - No parameters
  * @description Returns all asset accounts (checking or savings, not loans).
  * @example const accounts = Account.initAllAsset();
  * @memberof Account 
  */
  static initAllAsset() {
    const accounts = this._GlobalController.state[this._RecordType];
    
    return accounts.filter(item => (item.type === "checking" || item.type === "savings")).map((record) => {
        return this.initByType(record.id);
    });
  }
}


Account.define({
  "recordType": "accounts",
  "recordIDProp": "id",
  "className" : "Account",
  "recordProperties": [

   /** 
    * @name id
    * @instance
    * @property get {NUMBER} - gets the id of the account
    * @property set {NUMBER} - sets the id of the account
    * @memberof Account 
    */

    BCMSRecordProperty.mapToString("id", "_id"),

    /** 
     * @name name
     * @instance
     * @property get {STRING} - gets the name of the account
     * @property set {STRING} - sets the name of the account
     * @memberof Account 
     */

    BCMSRecordProperty.mapToString("name", "_name"),

    /** 
     * @name nickname
     * @instance
     * @property get {STRING} - gets the nickname of the account
     * @property set {STRING} - sets the nickname of the account
     * @memberof Account 
     */

    BCMSRecordProperty.mapToString("nickname", "_nickname"),

   /** 
    * @name number
    * @instance
    * @property get {STRING} - gets the number of the account
    * @property set {STRING} - sets the number of the account
    * @memberof Account 
    */

    BCMSRecordProperty.mapToString("number", "_number"),

   /** 
    * @name type
    * @instance
    * @property get {STRING} - gets the type of the account
    * @property set {STRING} - sets the type of the account
    * @memberof Account 
    */

    BCMSRecordProperty.mapToString("type", "_type"),

    /** 
     * @name canTransferFrom
     * @instance
     * @property get {STRING} - gets the typeID of the account
     * @property set {STRING} - sets the typeID of the account
     * @memberof Account 
     */

    BCMSRecordProperty.mapToString("canTransferFrom", "_canTransferFrom"),

   /** 
    * @name dateOpened
    * @instance
    * @property get {bcmsDate} - gets the dateOpened of the account
    * @property set {bcmsDate} - sets the dateOpened of the account
    * @memberof Account 
    */   

    BCMSRecordProperty.mapToDate("dateOpened", "_dateOpened"),

    /** 
     * @name balanceLabel
     * @instance
     * @property get {STRING} - gets the balanceLabel of the account
     * @property set {STRING} - sets the balanceLabel of the account
     * @memberof Account 
     */

    BCMSRecordProperty.mapToString("balanceLabel", "_balanceLabel"),

    /**
     * @name secondaryBalanceLabel
     * @instance
     * @property get {STRING} - gets the secondaryBalanceLabel of the account
     * @property set {STRING} - sets the secondaryBalanceLabel of the account
     * @memberof Account
     */
    BCMSRecordProperty.mapToString("secondaryBalanceLabel", "_secondaryBalanceLabel"),

  /** 
    * @name hasFastBalance
    * @instance
    * @property get {BOOLEAN} - gets the hasFastBalance of the account
    * @property set {BOOLEAN} - sets the hasFastBalance of the account
    * @memberof Account 
    */   

    BCMSRecordProperty.mapToBoolean("hasFastBalance", "_hasFastBalance"),

   /** 
    * @name isMobilePrimary
    * @instance
    * @property get {BOOLEAN} - gets the isMobilePrimary of the account
    * @property set {BOOLEAN} - sets the isMobilePrimary of the account
    * @memberof Account 
    */

    BCMSRecordProperty.mapToBoolean("isMobilePrimary", "_isMobilePrimary"),

   /** 
    * @name lastDownloadDate
    * @instance
    * @property get {bcmsDate} - gets the lastDownloadDate of the account
    * @property set {bcmsDate} - sets the lastDownloadDate of the account
    * @memberof Account 
    */

    BCMSRecordProperty.mapToDate("lastDownloadDate", "_lastDownloadDate"),

    /**
     * @name statementPreference
     * @instance
     * @property get {STRING} - gets the statementPreference of the account
     * @property set {STRING} - sets the statementPreference of the account
     * @memberof Account
     */
    BCMSRecordProperty.mapToString("statementPreference", "_statementPreference"),

    /**
     * @name isExternal
     * @instance
     * @property get {STRING} - gets the external value of the account
     * @property set {STRING} - sets the external value of the account
     * @memberof Account
     */
    BCMSRecordProperty.mapToBoolean("isExternal", "_isExternal"),

    /**
     * @name institutionName
     * @instance
     * @property get {STRING} - gets the institution name of the account
     * @property set {STRING} - sets the institution name of the account
     * @memberof Account
     */
    BCMSRecordProperty.mapToString("institutionName", "_institutionName"),

    /** 
    * @name routingNumber
    * @instance
    * @property get {NUMBER} - gets the routing number of the account
    * @property set {NUMBER} - sets the routing number of the account
    * @memberof Account 
    */
    BCMSRecordProperty.mapToNumber("routingNumber", "_routingNumber"),

    /**
     * @name externalStatus
     * @instance
     * @property get {STRING} - gets the external status of the account, like "active".
     * @property set {STRING} - sets the external status of the account, like "active".
     * @memberof Account
     */
    BCMSRecordProperty.mapToString("externalStatus", "_externalStatus"),

    /**
     * @name isBusiness
     * @instance
     * @property get {STRING} - gets the isBusiness value
     * @property set {STRING} - sets the isBusiness value
     * @memberof Account
     */
    BCMSRecordProperty.mapToString("isBusiness", "_isBusiness"),
  ]
})




